export interface EventListItem {
  id: string;
  title: string;
  description: string | null;
  event_type: string;
  status: string;
  privacy_level: string;
  meetup_time: string;
  kick_stands_up_time: string | null;
  location_name: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  latitude: number;
  longitude: number;
  max_participants: number | null;
  skill_level: string | null;
  riding_style: string | null;
  riding_style_min: string | null;
  riding_style_max: string | null;
  route_type: string | null;
  lane_filtering: string | null;
  host_notes: string | null;
  participant_rules: string | null;
  host_id: string;
  host_email: string | null;
  host_name: string | null;
  participant_count: number;
  sponsor_count: number;
  created_at: string;
  cancelled_at: string | null;
  cancellation_reason: string | null;
  view_count: number;
  invite_code: string | null;
  motorcycle_restrictions: any;
  allowed_gender: string | null;
  gps_start_latitude: number | null;
  gps_start_longitude: number | null;
  gps_end_latitude: number | null;
  gps_end_longitude: number | null;
  enable_route_sharing: boolean;
  chat_locked: boolean | null;
  chat_room_start_time: string | null;
}

export interface EventDetail extends EventListItem {
  participants: Array<{
    id: string;
    participant_id: string;
    status: string;
    created_at: string;
    interested_at: string | null;
    checked_in_at: string | null;
    name: string | null;
    email: string | null;
  }>;
  sponsors: Array<{
    id: string;
    name: string;
    website_url: string | null;
    logo_url: string | null;
    display_order: number;
    tier: string;
    tagline: string | null;
    fallback_color: string;
  }>;
  waypoints: Array<{
    id: string;
    name: string;
    latitude: number;
    longitude: number;
    order_index: number;
    waypoint_type: string;
    description: string | null;
  }>;
}

export interface HostOption {
  id: string;
  name: string;
  email: string;
}

export interface EventForm {
  host_id: string;
  title: string;
  description: string;
  event_type: string;
  privacy_level: string;
  meetup_time: string;
  kick_stands_up_time: string;
  location_name: string;
  address: string;
  city: string;
  state: string;
  latitude: string;
  longitude: string;
  max_participants: string;
  skill_level: string;
  riding_style: string;
  riding_style_min: string;
  riding_style_max: string;
  route_type: string;
  lane_filtering: string;
  host_notes: string;
  participant_rules: string;
  gps_start_latitude: string;
  gps_start_longitude: string;
  gps_end_latitude: string;
  gps_end_longitude: string;
  enable_route_sharing: boolean;
  chat_locked: boolean;
  chat_room_start_time: string;
  allowed_gender: string;
  invite_code: string;
  status: string;
  motorcycle_restrictions: {
    makes: string[];
    yearMin: string;
    yearMax: string;
    displacementMin: string;
    displacementMax: string;
  };
}

export interface SponsorForm {
  name: string;
  website_url: string;
  display_order: number;
  tier: string;
  tagline: string;
  fallback_color: string;
}

export interface WaypointForm {
  name: string;
  latitude: string;
  longitude: string;
  order_index: number;
  waypoint_type: string;
  description: string;
}

// ── Constants ────────────────────────────────────────────────────

export const EVENT_TYPES = [
  { value: 'cruise', label: 'Cruise', desc: 'Relaxed group rides, casual pace' },
  { value: 'hangout', label: 'Hangout', desc: 'Social focus, coffee/food stops' },
  { value: 'tour', label: 'Tour', desc: 'Longer scenic rides, day trips' },
  { value: 'workshop', label: 'Workshop', desc: 'Learning, maintenance, technical' },
];

export const PRIVACY_LEVELS = [
  { value: 'public', label: 'Public' },
  { value: 'friends_only', label: 'Friends Only' },
  { value: 'invite_only', label: 'Invite Only' },
  { value: 'private', label: 'Private' },
];

export const SKILL_LEVELS = [
  { value: '', label: 'Any' },
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' },
  { value: 'expert', label: 'Expert' },
];

export const RIDING_STYLES = [
  { value: '', label: 'Any' },
  { value: 'casual', label: 'Casual' },
  { value: 'spirited', label: 'Spirited' },
  { value: 'aggressive', label: 'Aggressive' },
];

export const RIDING_STYLE_VALUES = [
  { value: '', label: 'Not set' },
  { value: 'leisure', label: 'Leisure' },
  { value: 'casual', label: 'Casual' },
  { value: 'spirited', label: 'Spirited' },
  { value: 'sport', label: 'Sport' },
  { value: 'track', label: 'Track' },
];

export const ROUTE_TYPES = [
  { value: 'urban', label: 'Urban' },
  { value: 'city_and_highway', label: 'Highway' },
  { value: 'rural', label: 'Rural' },
  { value: 'twisties', label: 'Twisties' },
];

export const LANE_FILTERING = [
  { value: '', label: 'Not specified' },
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'situational', label: 'Situational' },
];

export const MOTORCYCLE_MAKES = [
  'Harley-Davidson', 'Indian', 'Honda', 'Yamaha', 'Kawasaki', 'Suzuki',
  'BMW', 'Ducati', 'Triumph', 'KTM', 'Aprilia', 'Moto Guzzi', 'Royal Enfield',
  'Can-Am', 'Husqvarna', 'MV Agusta', 'Benelli', 'CFMoto', 'Zero', 'LiveWire',
];

export const CHAT_START_OPTIONS = [
  { value: 'immediately', label: 'Immediately' },
  { value: '24_hours_before', label: '24 Hours Before' },
  { value: '3_days_before', label: '3 Days Before' },
  { value: 'at_event_start', label: 'At Event Start' },
];

export const STATUS_OPTIONS = [
  { value: 'scheduled', label: 'Scheduled', color: 'bg-blue-500/20 text-blue-400 border-blue-400/30' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-green-500/20 text-green-400 border-green-400/30' },
  { value: 'completed', label: 'Completed', color: 'bg-gray-500/20 text-gray-400 border-gray-400/30' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-red-500/20 text-red-400 border-red-400/30' },
];

export const EMPTY_FORM: EventForm = {
  host_id: '',
  title: '',
  description: '',
  event_type: 'cruise',
  privacy_level: 'public',
  meetup_time: '',
  kick_stands_up_time: '',
  location_name: '',
  address: '',
  city: '',
  state: '',
  latitude: '',
  longitude: '',
  max_participants: '',
  skill_level: '',
  riding_style: '',
  riding_style_min: '',
  riding_style_max: '',
  route_type: 'urban',
  lane_filtering: '',
  host_notes: '',
  participant_rules: '',
  gps_start_latitude: '',
  gps_start_longitude: '',
  gps_end_latitude: '',
  gps_end_longitude: '',
  enable_route_sharing: true,
  chat_locked: false,
  chat_room_start_time: 'immediately',
  allowed_gender: '',
  invite_code: '',
  status: 'scheduled',
  motorcycle_restrictions: { makes: [], yearMin: '', yearMax: '', displacementMin: '', displacementMax: '' },
};

export const EMPTY_SPONSOR: SponsorForm = {
  name: '',
  website_url: '',
  display_order: 1,
  tier: 'supporter',
  tagline: '',
  fallback_color: '#FF6B00',
};

export const EMPTY_WAYPOINT: WaypointForm = {
  name: '',
  latitude: '',
  longitude: '',
  order_index: 1,
  waypoint_type: 'waypoint',
  description: '',
};

// ── Component ────────────────────────────────────────────────────

